import React, { ReactElement, useMemo, useEffect } from "react";
import { Validator } from "../../models/Validator";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import { Row } from "@tanstack/table-core";
import CopyTextComponent from "../../components/copyText/CopyText.component";
import {
  formatAddress,
  formatAmountWithLabel,
  formatNumber,
  getYearlyROI,
} from "../../utils/formatter";
import { Group, Stack, Text, Tooltip } from "@mantine/core";
import { formatDate } from "../../utils/formatTime";
import styles from "./ValidatorsPage.module.scss";
import { getDaysRemaining } from "../../components/transactionDetailsTable/TransactionDetailsTable.component";
import { IconAlertTriangle, IconHelpSquareRounded } from "@tabler/icons-react";
import { OnlineIndicator } from "../../components/onlineIndicator/OnlineIndicator";
import { UNKNOWN_NAME } from "../../constants";

export default function ValidatorsTable({
  validators,
  timeZone,
}: {
  validators: Validator[] | null;
  timeZone: string;
}): ReactElement | null {
  const isValidatorPending = (validator: Validator): boolean => {
    const now = new Date();
    const startTime = new Date(validator.startTime);
    return now.getTime() < startTime.getTime();
  };

  const columns = useMemo<MRT_ColumnDef<Validator>[]>(
    () => [
      {
        accessorKey: "name",
        id: "name",
        header: "Name",
        Header: () => (
          <Group>
            <Text fw={500}>Name</Text>
          </Group>
        ),
        size: 150,
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enablePinning: true,
        filterFn: "myCustomFilterFn",
        sortingFn: (a, b) => {
          return (a.original.name ?? a.original.nodeID).localeCompare(
            b.original.name ?? a.original.nodeID
          );
        },
        Cell: ({ row }) => (
          <Stack gap={0}>
            <CopyTextComponent
              primaryColor={true}
              weight={"bold"}
              text={row.original.name ?? UNKNOWN_NAME}
              value={row.original.nodeID}
              link={`/validator/${row.original.nodeID}`}
            ></CopyTextComponent>
            <Text fz={10}>
              {formatAddress(row.original.nodeID, undefined, undefined, true)}
            </Text>
          </Stack>
        ),
      },
      {
        accessorKey: "totalStake",
        header: "Current stake",
        Header: () => (
          <Group>
            <Text fw={500}>Current stake</Text>
          </Group>
        ),
        size: 150,
        mantineTableHeadCellProps: {
          align: "right",
        },
        mantineTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ cell }) => (
          <Group gap={5} justify={"end"}>
            <div>{formatAmountWithLabel(cell.getValue<number>(), true, 0)}</div>
            {/*Only for empty space holder*/}
            {/*<div style={{ width: 16, height: 1 }}></div>*/}
          </Group>
        ),
      },
      {
        accessorKey: "freeSpace", //normal accessorKey
        header: "Free space",
        Header: () => (
          <Group gap={5} justify={"end"} ps={12}>
            <Text fw={500}>Free space</Text>
          </Group>
        ),
        mantineTableHeadCellProps: {
          align: "right",
        },
        mantineTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ cell, row }) => (
          <>
            <Group gap={5} justify={"end"}>
              <div>
                {formatAmountWithLabel(cell.getValue<number>(), true, 0)}
              </div>
              {row.original.capLimit &&
              row.original.capLimit < row.original.freeSpace ? (
                <Tooltip
                  label={`${
                    row.original.capLimit < 0
                      ? `Reward cap for all ${row.original.name} validators was reached.`
                      : `Reward cap will be reached for all ${row.original.name} validators after ${formatNumber(row.original.capLimit, true, 0)} FLR.`
                  }`}
                  multiline
                  w={220}
                  position="top"
                  events={{
                    hover: true,
                    focus: false,
                    touch: true,
                  }}
                >
                  <IconAlertTriangle
                    size={16}
                    style={{
                      flexShrink: 0,
                      color:
                        row.original.capLimit < 0
                          ? "var(--quinary)"
                          : "var(--mid-dark)",
                    }}
                  />
                </Tooltip>
              ) : (
                //Only for empty space holder
                <div style={{ width: 16, height: 1 }}></div>
              )}
            </Group>
          </>
        ),
      },
      {
        accessorKey: "endTime",
        header: "End time",
        Header: () => (
          <Group>
            <Text fw={500}>End time</Text>
          </Group>
        ),
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        size: 250,
        Cell: ({ cell }) => (
          <>
            {cell.getValue<string>() === null ? (
              <span> No date provided</span>
            ) : (
              <>
                {isValidatorPending(cell.row.original) ? (
                  <Stack className={styles.stakingStartTimeContainer}>
                    <div>Staking starts on</div>
                    <span>
                      {
                        formatDate(new Date(cell.getValue<string>()), timeZone)
                          .formattedDateTime
                      }
                      <span className={styles.unitLabel}>
                        {" "}
                        {
                          formatDate(new Date(cell.getValue<string>()), timeZone)
                            .utcOffset
                        }
                      </span>
                    </span>
                  </Stack>
                ) : (
                  <span>
                    {
                      formatDate(new Date(cell.getValue<string>()), timeZone)
                        .formattedDateTime
                    }
                    &nbsp;
                    <span className={styles.unitLabel}>
                      {formatDate(new Date(cell.getValue<string>()), timeZone).utcOffset}
                    </span>
                  </span>
                )}
              </>
            )}
          </>
        ),
      },
      {
        accessorKey: "daysRemaining",
        header: "Remaining",
        Header: () => (
          <Group>
            <Text fw={500}>Remaining</Text>
          </Group>
        ),
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        size: 140,
        sortingFn: (a, b) => {
          return (
            new Date(a.original.endTime).getTime() -
            new Date(b.original.endTime).getTime()
          );
        },
        Cell: ({ row }) => (
          <>
            {isValidatorPending(row.original) ? (
              <span className={styles.notAvailable}>n/a</span>
            ) : (
              <>{getDaysRemaining(row.original.endTime, false, true)}</>
            )}
          </>
        ),
      },
      {
        accessorKey: "feePercentage",
        header: "Fee",
        Header: () => (
          <Group>
            <Text fw={500}>Fee</Text>
          </Group>
        ),
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        size: 120,
        Cell: ({ cell }) => (
          <>{formatNumber(cell.getValue<number>() / 1e4, false, 1) + "%"}</>
        ),
      },
      {
        accessorKey: "uptime",
        header: "Uptime",
        Header: () => (
          <>
            <Group justify={"start"} gap={5}>
              <Text fw={500}>Uptime</Text>
              <Tooltip
                label={"Measured uptime for current epoch."}
                position="top"
                events={{
                  hover: true,
                  focus: false,
                  touch: true,
                }}
              >
                <IconHelpSquareRounded size={20} style={{ flexShrink: 0 }} />
              </Tooltip>
            </Group>
          </>
        ),
        size: 140,
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        Cell: ({ row }) => (
          <>
            {isValidatorPending(row.original) ? (
              <span className={styles.notAvailable}>n/a</span>
            ) : (
              <Group justify={"space-between"} maw={80} gap={0}>
                <span>
                  {formatNumber(row.original.uptime * 100, false, 2) + "%"}
                </span>
                <OnlineIndicator online={row.original.online} />
              </Group>
            )}
          </>
        ),
      },
      {
        accessorKey: "roi",
        header: "APY",
        Header: () => (
          <Group gap={5}>
            <Text fw={500}>APY</Text>
            <Tooltip
              label={
                "Estimated annual percentage return based on rewards in latest Epoch."
              }
              multiline
              w={220}
              position="top"
              events={{
                hover: true,
                focus: false,
                touch: true,
              }}
            >
              <IconHelpSquareRounded size={20} style={{ flexShrink: 0 }} />
            </Tooltip>
          </Group>
        ),
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        size: 110,
        sortingFn: (a, b) => {
          return (
            (a.original.roi === null || a.original.roi === undefined
              ? -1
              : a.original.roi) -
            (b.original.roi === null || b.original.roi === undefined
              ? -1
              : b.original.roi)
          );
        },
        Cell: ({ row }) => (
          <>
            <span>
              {row.original.roi !== undefined && row.original.roi !== null
                ? formatNumber(getYearlyROI(row.original.roi * 100), false, 2) +
                  "%"
                : "N/A"}
            </span>
          </>
        ),
      },
    ],
    [validators, timeZone]
  );

  const table = useMantineReactTable({
    columns: validators ? columns : [],
    data: validators || [], //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    mantineTableProps: {
      style: {
        tableLayout: "fixed",
        fontWeight: 500,
        fontSize: "var(--module-size-14)",
        color: "var(--mid-dark)",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        fontWeight: 500,
        fontSize: 15,
      },
    },
    state: {
      isLoading: !validators,
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: "none",
    },
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    // enableFilters: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableColumnPinning: false,
    enableRowNumbers: true,
    positionGlobalFilter: "left",
    initialState: {
      sorting: [{ id: "name", desc: false }],
      density: "xs",
      // columnPinning: { left: ["name"] },
      showGlobalFilter: true,
    },
    mantinePaginationProps: {
      showRowsPerPage: false,
      withEdges: false,
    },
    filterFns: {
      myCustomFilterFn: (
        row: Row<Validator>,
        id: string,
        filterValue: string
      ) => {
        const containsAddress = row.original.nodeID
          .toLowerCase()
          .includes(filterValue.toLowerCase());

        const containsAddressName = row.original.name
          ? row.original.name.toLowerCase().includes(filterValue.toLowerCase())
          : false;

        return containsAddress || containsAddressName;
      },
    },
    globalFilterFn: "myCustomFilterFn",
  });

  return (
    <div id="validatorsTable">
      <MantineReactTable table={table} />
    </div>
  );
}
